import httpClient from '../../../shared/lib/httpClient';
import { ENDPOINTS } from '../../../shared/services/api/endpoints';
import { showToast } from '../../../shared/services/toast';

/**
 * Get current user profile
 * @returns {Promise<Object>} User profile data
 */
export async function getMyProfile() {
  const response = await httpClient.get(ENDPOINTS.IDENTITY.PROFILE);
  return response.data;
}

/**
 * Update user profile
 * @param {Object} profileData - Profile data to update
 * @returns {Promise<{ success: boolean, message?: string, data?: any }>}
 */
export async function updateProfile(profileData) {
  try {
    const response = await httpClient.put(
      ENDPOINTS.IDENTITY.UPDATE_PROFILE,
      profileData,
      { timeout: 20000 }
    );
    const message = response.data?.message || 'Profile updated successfully!';
    showToast.success(message);
    return { success: true, message, data: response.data?.data };
  } catch (error) {
    const message = error.response?.data?.message || 'Failed to update profile.';
    showToast.error(message);
    return { success: false, message };
  }
}

/**
 * Whether a username is already held by another account — soft-deleted ones included, the same
 * rule as the server's unique index. Advisory only: the profile save re-checks and answers 409.
 * @param {string} username - Username to look up
 * @returns {Promise<boolean>} True when the username is taken
 */
export async function checkUsernameExists(username) {
  const response = await httpClient.get(ENDPOINTS.USER.CHECK_USERNAME, { params: { username } });
  return response.data?.data?.exists === true;
}

/**
 * Get regions by country code
 * @param {string|number} countryCode - Country code
 * @returns {Promise<Array<{ id: string, name: string }>>}
 */
export async function getRegions(countryCode) {
  if (!countryCode) {
    return [];
  }

  try {
    const response = await httpClient.get(ENDPOINTS.IDENTITY.GET_REGIONS_BY_COUNTRY(countryCode));
    return response.data?.data?.regions || [];
  } catch {
    showToast.error('Failed to load regions.');
    return [];
  }
}

/**
 * Get all countries
 * @returns {Promise<{ countries: Array<{ id: string, name: string, code: string }>}>}
 */
export async function getCountries() {
  try {
    const response = await httpClient.get(ENDPOINTS.IDENTITY.GET_COUNTRIES);
    return response.data.data.countries;
  } catch {
    showToast.error('Failed to load countries.');
    return [];
  }
}

/**
 * Fetch main contact options for dropdown
 * @param {number} clientId - HaloPSA client ID
 * @param {number} siteId - HaloPSA site ID
 * @returns {Promise<Array<{ id: number, username: string, isCurrentMainContact: boolean }>>}
 */
export async function getMainContactOptions(clientId, siteId) {
  try {
    // Build URL with query parameters for client and site IDs
    const url = `${ENDPOINTS.IDENTITY.MAIN_CONTACT_OPTIONS}?clientId=${clientId}&siteId=${siteId}`;
    const response = await httpClient.get(url);
    return response.data?.data || [];
  } catch (error) {
    console.error('Failed to fetch main contact options:', error);
    return [];
  }
}

export async function deleteFirmAccount(firmId) {
  const response = await httpClient.delete(ENDPOINTS.IDENTITY.DELETE_FIRM(firmId), { timeout: 20000 });
  const message = response.data?.message || 'Account deleted successfully.';
  showToast.success(message);
  return { success: true, message, data: response.data?.data };
}

/**
 * Enable/disable MFA for the current firm (account owner only).
 * @param {boolean} enabled - Whether MFA should be enabled
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export async function updateFirmMfa(enabled) {
  try {
    const response = await httpClient.put(ENDPOINTS.FIRM.UPDATE_MFA, { enabled }, { timeout: 20000 });
    const message = response.data?.message || (enabled ? 'MFA enabled' : 'MFA disabled');
    showToast.success(message);
    return { success: true, message };
  } catch (error) {
    const message = error.response?.data?.message || 'Failed to update MFA setting.';
    showToast.error(message);
    return { success: false, message };
  }
}

/**
 * Transfer account ownership to another user
 * @param {number} newOwnerUserId - The user ID of the new account owner
 * @returns {Promise<Object>} Response with transfer result
 */
export async function transferOwnership(newOwnerUserId) {
  const response = await httpClient.post(ENDPOINTS.IDENTITY.TRANSFER_OWNERSHIP, {
    newOwnerUserId
  }, { timeout: 30000 });
  return response.data;
}

/**
 * Get the current user's authenticator (TOTP) configuration status.
 * The backend returns { hasAuthenticator }; we translate to { hasTotpConfigured } so the shape
 * change stays confined to this service and AuthenticatorSection reads the same field it always has.
 * @returns {Promise<{ hasTotpConfigured: boolean }>}
 */
export async function getAuthenticatorStatus() {
  try {
    const response = await httpClient.get(ENDPOINTS.IDENTITY.AUTHENTICATOR);
    const payload = response.data?.data || {};
    return { hasTotpConfigured: !!payload.hasAuthenticator };
  } catch (error) {
    console.error('Failed to get authenticator status:', error);
    return { hasTotpConfigured: false };
  }
}

/**
 * Remove the current user's authenticator (TOTP) credential.
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export async function removeAuthenticator() {
  try {
    const response = await httpClient.delete(ENDPOINTS.IDENTITY.AUTHENTICATOR);
    const message = response.data?.message || 'Authenticator removed successfully.';
    showToast.success(message);
    return { success: true, message };
  } catch (error) {
    const message = error.response?.data?.message || 'Failed to remove authenticator';
    showToast.error(message);
    return { success: false, message };
  }
}
